//react-feature-flags
import { jsx } from 'react/jsx-runtime';
import { createContext, useState, useRef, useEffect, useContext, useCallback } from 'react';
import { UnleashClient } from 'unleash-proxy-client';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var FlagContext = createContext({});

var DEFAULT_UNLEASH_PROXY_URL = 'https://unleash-proxy.cognitedata-production.cognite.ai/proxy';
var FlagProvider = function (_a) {
    var appName = _a.appName, apiToken = _a.apiToken, children = _a.children, _b = _a.proxyUrl, proxyUrl = _b === void 0 ? DEFAULT_UNLEASH_PROXY_URL : _b, refreshInterval = _a.refreshInterval /* This is defaulted to 30 sec by the unleash-proxy-client */, projectName = _a.projectName, organizationId = _a.organizationId, disableMetrics = _a.disableMetrics;
    var _c = __read(useState(false), 2), isClientReady = _c[0], setIsClientReady = _c[1];
    var client = useRef(new UnleashClient({
        appName: appName,
        clientKey: apiToken,
        url: proxyUrl,
        refreshInterval: refreshInterval,
        disableMetrics: disableMetrics,
    }));
    useEffect(function () {
        if (projectName) {
            var context = {
                /*
                 * For simplicity and unsupported strategies in the unleash proxy,
                 * we use project as userId to enable a default rollout strategy
                 */
                userId: projectName,
            };
            if (organizationId) {
                context.organizationId = organizationId;
            }
            client.current.updateContext(context);
            client.current.on('ready', function () {
                setIsClientReady(true);
            });
            client.current.start();
        }
        return function () {
            client.current.stop();
        };
    }, [projectName, organizationId]);
    if (!isClientReady)
        return null;
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    var contextValue = { client: client.current, isClientReady: isClientReady };
    return (jsx(FlagContext.Provider, __assign({ value: contextValue }, { children: children })));
};

var useFlag = function (flagName, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.fallback, fallback = _c === void 0 ? false : _c, _d = _b.forceRerender, forceRerender = _d === void 0 ? false : _d;
    var _e = useContext(FlagContext), client = _e.client, isClientReady = _e.isClientReady;
    var _f = __read(useState(function () {
        var value = client && client.isEnabled(flagName);
        if (value === undefined) {
            value = fallback;
        }
        return value;
    }), 2), isEnabled = _f[0], setIsEnabled = _f[1];
    var update = useCallback(function () {
        setIsEnabled(client && client.isEnabled(flagName));
    }, [flagName, client]);
    useEffect(function () {
        if (forceRerender) {
            if (client) {
                client.on('update', update);
            }
        }
        return function () {
            if (client) {
                client.off('update', update);
            }
        };
    }, [forceRerender, update, client]);
    return { isClientReady: isClientReady, isEnabled: isEnabled };
};

var useVariant = function (toggleName) {
    var client = useContext(FlagContext).client;
    var _a = __read(useState(''), 2), variantValue = _a[0], setVariantValue = _a[1];
    useEffect(function () {
        var _a;
        if (!client) {
            setVariantValue('');
        }
        else {
            var variant = client.getVariant(toggleName);
            setVariantValue(((_a = variant.payload) === null || _a === void 0 ? void 0 : _a.value) || '');
        }
    }, [client]);
    return variantValue;
};

export { FlagProvider, useFlag, useVariant };
